import { IconButton, TextField } from "@mui/material";
import * as React from "react";

import { useAnalytics } from "@/providers";
import {
  LockIcon as Lock,
  LockOpenIcon as LockOpen,
} from "@/shared/foundation/icons/mui";
import { useVizDispatch } from "@/shared/state/visualization";
import { setPlotPanelConfig } from "@/shared/state/visualization/actions";
import {
  PlotAxisConfig,
  PlotPanelConfig,
  PlotPanelState,
  PlotYAxis,
} from "@/shared/state/visualization/schema/v1";

import sectionStyles from "../SettingsSection.module.css";

import styles from "./AxisRangeInput.module.css";

type AxisBounds = Exclude<PlotAxisConfig["bounds"], undefined>;

interface AxisRangeInputProps {
  panelId: string;
  state: PlotPanelState;
  axis: PlotYAxis;
  currentBounds: AxisBounds | undefined;
}

const BLUR_DELAY_MS = 10;

function areBoundsInvalid(bounds: AxisBounds | undefined) {
  return (
    !bounds ||
    bounds.min >= bounds.max ||
    !Number.isFinite(bounds.min) ||
    !Number.isFinite(bounds.max)
  );
}

/**
 * Allows changing the range of an axis.
 *
 * Currently this only supports changing the y axis of a chart.
 *
 * TODO(ENG-1429): Support changing the range of multiple Y axes and the X axis.
 */
export function AxisRangeInput({
  panelId,
  currentBounds,
  axis,
  state,
}: AxisRangeInputProps) {
  const dispatch = useVizDispatch();
  const { trackEvent } = useAnalytics();

  const [inputMin, setInputMin] = React.useState(`${currentBounds?.min ?? ""}`);
  const [inputMax, setInputMax] = React.useState(`${currentBounds?.max ?? ""}`);
  const [minFocused, setMinFocused] = React.useState(false);
  const minBlurRef = React.useRef<number>();
  const [maxFocused, setMaxFocused] = React.useState(false);
  const maxBlurRef = React.useRef<number>();
  const [boundsInvalid, setBoundsInvalid] = React.useState<boolean>(false);

  React.useEffect(
    function applyVizStateToTextFields() {
      // While the user is editing bounds, don't propagate app-level state changes down to this view.
      if (maxFocused || minFocused) {
        return;
      }
      setInputMin(`${currentBounds?.min ?? ""}`);
      setInputMax(`${currentBounds?.max ?? ""}`);
      setBoundsInvalid(areBoundsInvalid(currentBounds));
    },
    [currentBounds, minFocused, maxFocused, setInputMin, setInputMax],
  );

  const validateAndDispatch = React.useCallback(
    function onRangeUpdated(nextBounds: AxisBounds) {
      // Validate the bounds generated from the text inputs.
      // Dispatch if the new bounds are both valid and different from the current state.
      if (areBoundsInvalid(nextBounds)) {
        setBoundsInvalid(true);
      } else {
        setBoundsInvalid(false);
        if (
          nextBounds.min !== currentBounds?.min ||
          nextBounds.max !== currentBounds?.max
        ) {
          const nextConfig: Partial<PlotPanelConfig> =
            axis === "y"
              ? {
                  yAxis: { bounds: nextBounds },
                }
              : {
                  extraYAxes: {
                    [axis]: { bounds: nextBounds },
                  },
                };
          dispatch(setPlotPanelConfig(panelId, nextConfig));
        }
      }
    },
    [panelId, dispatch, currentBounds, axis],
  );

  const onMinUpdated = React.useCallback(
    function onMinUpdated(text: string) {
      setInputMin(text);
      // Prepare a bounds update object.
      const nextBounds: AxisBounds = {
        locked: currentBounds?.locked,
        min: Number.parseFloat(text),
        max: Number.parseFloat(inputMax),
        setBy: "settings",
      };
      validateAndDispatch(nextBounds);
    },
    [setInputMin, inputMax, validateAndDispatch, currentBounds],
  );

  const onMaxUpdated = React.useCallback(
    function onMaxUpdated(text: string) {
      setInputMax(text);
      // Prepare a bounds update object.
      const nextBounds: AxisBounds = {
        locked: currentBounds?.locked,
        min: Number.parseFloat(inputMin),
        max: Number.parseFloat(text),
        setBy: "settings",
      };
      validateAndDispatch(nextBounds);
    },
    [setInputMax, inputMin, validateAndDispatch, currentBounds],
  );

  // Delay blur events on both text fields to allow time for the user to tab or click between them.
  // While either text field is focused, the text fields will treat their state as the
  // source of truth over the viz config state, allowing the user to do things like input
  // and then fix invalid bounds entries.
  const blurMinWithDelay = React.useCallback(
    function blurMin() {
      if (minBlurRef.current !== undefined) {
        window.clearTimeout(minBlurRef.current);
      }
      minBlurRef.current = window.setTimeout(() => {
        setMinFocused(false);
        minBlurRef.current = undefined;
      }, BLUR_DELAY_MS);
    },
    [setMinFocused],
  );

  const blurMaxWithDelay = React.useCallback(
    function blurMax() {
      if (maxBlurRef.current !== undefined) {
        window.clearTimeout(maxBlurRef.current);
      }
      maxBlurRef.current = window.setTimeout(() => {
        setMaxFocused(false);
        maxBlurRef.current = undefined;
      }, BLUR_DELAY_MS);
    },
    [setMaxFocused],
  );

  React.useEffect(function clearBlurTimeoutsOnUnmount() {
    return function onUnmount() {
      if (minBlurRef.current !== undefined) {
        window.clearTimeout(minBlurRef.current);
      }
      if (maxBlurRef.current !== undefined) {
        window.clearTimeout(maxBlurRef.current);
      }
    };
  }, []);

  const isLocked = !!state.config?.yAxis?.bounds?.locked;
  return (
    <div key={`${panelId}-y-axis-range`} className={sectionStyles.inputRow}>
      <TextField
        disabled={isLocked}
        className={sectionStyles.input}
        value={inputMin}
        label="Range Min"
        onFocus={() => setMinFocused(true)}
        onBlur={blurMinWithDelay}
        error={boundsInvalid}
        inputProps={{
          className: styles.input,
          "aria-label": "Y-Axis Range Minimum",
          maxLength: 50, // 50 character limit chosen as a standard practice
        }}
        InputProps={{ className: styles.input }}
        InputLabelProps={{ className: styles.input, shrink: true }}
        margin="dense"
        onChange={(event) => onMinUpdated(event.target.value)}
        size="small"
      />
      <TextField
        disabled={isLocked}
        className={sectionStyles.input}
        value={inputMax}
        label="Range Max"
        onFocus={() => setMaxFocused(true)}
        onBlur={blurMaxWithDelay}
        error={boundsInvalid}
        inputProps={{
          className: styles.input,
          "aria-label": "Y-Axis Range Maximum",
          maxLength: 50, // 50 character limit chosen as a standard practice
        }}
        InputProps={{ className: styles.input }}
        InputLabelProps={{
          className: styles.input,
          shrink: true,
        }}
        margin="dense"
        onChange={(event) => onMaxUpdated(event.target.value)}
        size="small"
      />
      <div className={styles.iconGridContainer}>
        <IconButton
          className={styles.iconGrid}
          onClick={() => {
            const yAxisBounds = state.config?.yAxis?.bounds;
            if (!yAxisBounds) {
              return;
            }
            const nextLock = !yAxisBounds.locked;
            trackEvent("PlotPanelAxisLockChanged", {
              axis: "y",
              locked: nextLock,
            });
            dispatch(
              setPlotPanelConfig(panelId, {
                yAxis: {
                  bounds: {
                    ...yAxisBounds,
                    locked: nextLock,
                    setBy: "settings",
                  },
                },
              }),
            );
          }}
        >
          <Lock
            fontSize="small"
            className={isLocked ? styles.iconVisible : styles.iconHidden}
          />
          <LockOpen
            fontSize="small"
            className={!isLocked ? styles.iconVisible : styles.iconHidden}
          />
        </IconButton>
      </div>
    </div>
  );
}
